import React, { useState, useEffect } from 'react';
import filter from "../../filter";
import shallowequal from "shallowequal";
import { calculateTotalColumn, renderPrintFooter, updateParams } from "../../utils";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { chartDefaultColors } from "../../chartDefaultColors";
import { buyerStateChartOptions } from "./chartOptions";
import { fetchData } from '../Service/graphQlService';
import { GET_BUYER_STATE_BREAKDOWN } from '../Graphql/Queries/propertiesQuery';

const cloneDeep = require('lodash.clonedeep');

const BuyerStateBreakdown = (props) => {
    const [state, setState] = useState({
        query: {},
        table: {
            header: ['Buyer State', '# of Properties', '%'],
            rows: [],
            total: ['Total', null, '100%'],
            criteria: [],
        },
        compareToTable: {
            header: ['Buyer State', '# of Properties', '%'],
            rows: [],
            total: ['Total', null, '100%'],
            criteria: [],
        },
        buyerStateChartOptions: cloneDeep(buyerStateChartOptions),
        isPrint: false,
        printBackground: '',
    });

    useEffect(() => {
        const printBackground = `url('${arc.path.media+'Transaction_Background.png'}')`;
        setState(prevState => ({ ...prevState, printBackground }));
        
        fetch(props.location.query);
        
        window.history.replaceState(null, '');
        
        const handleBeforePrint = () => {
            setState(prevState => ({ ...prevState, isPrint: true }));
        };
        
        const handleAfterPrint = () => {
            setState(prevState => ({ ...prevState, isPrint: false }));
        };
        
        window.onbeforeprint = handleBeforePrint;
        window.onafterprint = handleAfterPrint;
        
        // Cleanup event listeners
        return () => {
            window.onbeforeprint = null;
            window.onafterprint = null;
        };
    }, []);

    useEffect(() => {
        if (!shallowequal(props.location.query, state.query)) {
            fetch(props.location.query);
        }
    }, [props.location.query]);

    const fetch = (q) => {
        const query = {...q};
        query.order = ['TOTAL_PROPERTIES.desc', 'COMPANY_STATE.asc'];
        const firstQuery = {...query};
        const secondQuery = {...query};

        let options = {}, optionsYearToCompare = {};
        if (query.comparison_min || query.comparison_max) {
            delete firstQuery.comparison_min;
            delete firstQuery.comparison_max;
            delete secondQuery.base_min;
            delete secondQuery.base_max;
            options = filter(firstQuery, 'buyer-breakdown');
            optionsYearToCompare = filter(secondQuery, 'buyer-breakdown');
            
            const paramUpdateKeys = [
                'status', 'hsc_status', 'type', 'category', 
                'product_type', 'zip', 'market', 
                'territory_market', 'territory'
            ];
            
            paramUpdateKeys.forEach(key => {
                options.param = updateParams(options.param, key, 
                    key === 'status' ? 8 :
                    key === 'hsc_status' ? 4 :
                    key === 'type' ? 2 :
                    key === 'category' ? 5 :
                    key === 'product_type' ? 5 :
                    key === 'zip' ? 120 :
                    key === 'market' ? 9 :
                    key === 'territory_market' ? 11 :
                    key === 'territory' ? 4 : null
                );
                optionsYearToCompare.param = updateParams(optionsYearToCompare.param, key, 
                    key === 'status' ? 8 :
                    key === 'hsc_status' ? 4 :
                    key === 'type' ? 2 :
                    key === 'category' ? 5 :
                    key === 'product_type' ? 5 :
                    key === 'zip' ? 120 :
                    key === 'market' ? 9 :
                    key === 'territory_market' ? 11 :
                    key === 'territory' ? 4 : null
                );
            });
        } else {
            options = filter(query, 'buyer-breakdown');
            
            const paramUpdateKeys = [
                'status', 'hsc_status', 'type', 'category', 
                'product_type', 'zip', 'market', 
                'territory_market', 'territory'
            ];
            
            paramUpdateKeys.forEach(key => {
                options.param = updateParams(options.param, key, 
                    key === 'status' ? 8 :
                    key === 'hsc_status' ? 4 :
                    key === 'type' ? 2 :
                    key === 'category' ? 5 :
                    key === 'product_type' ? 5 :
                    key === 'zip' ? 120 :
                    key === 'market' ? 9 :
                    key === 'territory_market' ? 11 :
                    key === 'territory' ? 4 : null
                );
            });
        }

        const requests = [fetchData(GET_BUYER_STATE_BREAKDOWN, { listOptions: options.param})];
        if (query.comparison_min || query.comparison_max) {
            requests.push(fetchData(GET_BUYER_STATE_BREAKDOWN, { listOptions: optionsYearToCompare.param}))
        }
        
        Promise.all(requests).then(res => {
            if (res && res[0] && res[0].data && res[0].data.getPropertiesByBuyerState && Array.isArray(res[0].data.getPropertiesByBuyerState)) {
                res[0].data.getPropertiesByBuyerState = res[0].data.getPropertiesByBuyerState.map((prop)=>{
                    delete prop.__typename
                    return prop
                })
                const table = buildTable(state.table, res[0].data.getPropertiesByBuyerState, firstQuery);
                if (res && res[1] && res[1].data && res[1].data.getPropertiesByBuyerState && Array.isArray(res[1].data.getPropertiesByBuyerState)) {
                    res[1].data.getPropertiesByBuyerState = res[1].data.getPropertiesByBuyerState.map((prop)=>{
                        delete prop.__typename
                        return prop
                    })
                    const compareToTable = buildTable(state.compareToTable, res[1].data.getPropertiesByBuyerState, secondQuery);
                    setState(prevState => ({...prevState, table, compareToTable, query}));
                    return;
                }
                setState(prevState => ({...prevState, table, query}));
                return;
            }
            setState(prevState => ({...prevState, query}));
        });
    };

    const parseKey = (key) => {
        let k = key;
        if (k.includes('_min')) {
            k = k.replace('_min', '');
        }
        if (k.includes('_max')) {
            k = k.replace('_max', '');
        }
        k = k.split('_').join(' ');
        return k[0] + k.slice(1).toLowerCase();
    };

    const parseValue = (value) => {
        return value[0].toUpperCase() + value.slice(1).toLowerCase();
    };

    const parseCriteria = (q) => {
        const query = {...q};
        const criteria = [];
        delete query.order;

        Object.keys(query).forEach(key => {
            if (key.includes('min')) {
                const c = [query[key]];
                const k = key.replace('_min', '_max');
                if (query[k]) {
                    c.push(query[k]);
                } else {
                    if (isNaN(query[key].replace(',',''))) {
                        c.push('today');
                    } else {
                        c.push('max');
                    }
                }
                const value = c.join(' - ');
                if (['base_min', 'comparison_min'].includes(key)) {
                    criteria.unshift(`${parseKey(key)}: ${value}`);
                    return;
                }
                criteria.push(`${parseKey(key)}: ${value}`);
                return;
            }
            if (key.includes('max')) {
                const c = []
                const k = key.replace('_max', '_min');
                if (query[k]) {
                    return;
                } else {
                    c.push('min');
                    c.push(query[key]);
                }
                const value = c.join(' - ');
                if (['base_max', 'comparison_max'].includes(key)) {
                    criteria.unshift(`${parseKey(key)}: ${value}`);
                    return;
                }
                criteria.push(`${parseKey(key)}: ${value}`);
                return;
            }
            let value = query[key];
            if (['category'].includes(key)) {
                value = parseValue(value);
            }
            criteria.push(`${parseKey(key)}: ${value}`);
        });
        return criteria;
    };

    const buildTable = (prevState, data, query) => {
        const table = {...prevState};
        table.rows = data;
        table.total[1] = calculateTotalColumn(table.rows, 'total_properties');
        table.rows.map(row => {
            const percentage = row.total_properties / table.total[1] * 100;
            row.percentage = percentage ? percentage.toFixed(2) : 0;
        });
        table.criteria = parseCriteria(query);
        return table;
    };

    const renderTable = (table) => {
        return (
            <div className='table-container'>
                <table className='table'>
                    <thead>
                    <tr>
                        {
                            table.header.map((col, index) => {
                                return <th key={index} className='th'>{col}</th>
                            })
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        table.rows.map((row, index) => {
                            return (
                                <tr key={index}>
                                    {
                                        Object.keys(row).map((key, index) => {
                                            const hasPercent = key === 'percentage';
                                            return <td key={`${key}-${index}`} className='td'>{row[key]}{hasPercent ? '%' : ''}</td>
                                        })
                                    }
                                </tr>
                            )
                        })
                    }
                    </tbody>
                    <tfoot>
                    <tr>
                        {
                            table.total.map((col, index) => {
                                return <td key={index} className='td total'>{col}</td>
                            })
                        }
                    </tr>
                    </tfoot>
                </table>
                <div className='criteria'>
                    {
                        table.criteria.map((criteria, index) => {
                            const comma = index !== table.criteria.length - 1 ? ',' : null;
                            return <div key={index}>{criteria}{comma}</div>
                        })
                    }
                </div>
            </div>
        );
    };

    const renderPieChart = (table, buyerStateChartOptions) => {
        let index = 0, otherPercentage = 0, otherCount = 0;
        const chartOptions = cloneDeep(buyerStateChartOptions);

        if (table.rows.length) {
            table.rows.forEach((row, rowIndex) => {
                if (rowIndex < 3) {
                    chartOptions.series[0].data.push({
                        name: row.company_state,
                        y: parseInt(row.total_properties),
                        color: chartDefaultColors[index++]
                    });
                } else {
                    otherPercentage += parseFloat(row.percentage);
                    otherCount += parseInt(row.total_properties);
                }
            });

            if (otherPercentage && otherCount) {
                chartOptions.series[0].data.push({
                    name: 'Other',
                    y: otherCount,
                    color: chartDefaultColors[index]
                });
            }
        }

        if (chartOptions.series[0].data.length) {
            return <HighchartsReact highcharts={Highcharts} options={chartOptions}/>
        }
        return <div>No results</div>
    };

    const renderData = (table, buyerStateChartOptions, year, isPrint) => {
        return (
            <div className='breakdown-container'>
                <h3 className='title'>Buyer State Breakdown - {year}</h3>
                <div className='breakdown-data'>
                    {renderTable(table)}
                    <div className='chart-container' style={{width: isPrint ? '220px' : '250px'}} key={Math.random()}>
                        {renderPieChart(table, buyerStateChartOptions)}
                    </div>
                </div>
            </div>
        )
    };

    const { table, compareToTable, query, isPrint, printBackground } = state;
    const year = query && query.base_min ? new Date(query.base_min).getFullYear() : null;
    const comparisonYear = query && query.comparison_min ? new Date(query.comparison_min).getFullYear() : null;
    
    if (query) {
        delete query.order;
    }

    return (
        <div className='BuyerStateBreakdown' style={{backgroundImage: isPrint && printBackground}}>
            {renderData(table, state.buyerStateChartOptions, year, isPrint)}
            {comparisonYear ? renderData(compareToTable, state.buyerStateChartOptions, comparisonYear, isPrint) : null}
            {renderPrintFooter(true)}
            <div className='preload-print-background' style={{backgroundImage: printBackground}}/>
        </div>
    );
};

export default BuyerStateBreakdown;